<template>
  <DetailPage full-width>
    <template #left-nav>
      <button class="link link-main icon-link" @click="emit('onBack')">
        <Icon icon="left" />
        {{ $t('PUBLISHING.DETAIL.PRODUCTS.ADVANCED.BACK_TO_SELLABLES') }}
      </button>
    </template>
    <template #central-nav>
      <h2>{{ $t('PUBLISHING.DETAIL.PRODUCTS.ADVANCED.CATALOG.HEADING') }}</h2>
      <CompositionModeBadge :composition-mode="compositionMode" />
    </template>
    <template #content>
      <div class="catalog" :class="{ 'has-details': detailProduct !== null }">
        <button
          v-if="detailProduct"
          class="sprd-btn-light assortment-toggle-button"
          @click="() => (detailProduct = null)"
        >
          <Icon icon="left" />
        </button>
        <div v-if="!detailProduct" class="assortment-wrapper">
          <div class="assortment overflow-y">
            <ProductSettingsAssortmentFilter
              :assortment="assortment"
              :product-types="allProductTypes"
              :filters="filters"
              :active-first-level="activeFirstLevel"
              :active-second-level="activeSecondLevel"
              @update-first-level="onFilterUpdateFirstLevel"
              @update-second-level="onFilterUpdateSecondLevel"
            />
          </div>
        </div>

        <div class="catalog-content overflow-y">
          <div>
            <ProductSettingsFilters
              :product-types="selectedProductTypes"
              :filters="filters"
              @update-appearance-groups="onFilterUpdateAppearanceGroups"
              @update-target-groups="onFilterUpdateTargetGroups"
              @update-brands="onFilterUpdateBrands"
              @update-hotspots="onFilterUpdateHotspots"
              @update-size-groups="onFilterUpdateSizeGroups"
            />
            <div class="product-type-list">
              <LazyAssortmentCard
                v-for="productType in selectedProductTypesFiltered"
                :key="productType.id"
                :product-type-id="productType.id"
                :view-id="productType.defaultViewId"
                :default-appearance-id="getRelevantAppearanceId(productType)"
                :appearance-ids="productType.appearanceIds"
                image-overlay-icon="plus"
                :context-menu-options="contextMenuOptions"
                @context-menu-click="
                  (option: { key: string; name: string }) =>
                    contextMenuClick(productType, option)
                "
                @edit="() => editProductType(productType)"
              />
            </div>
          </div>
        </div>
        <div v-if="detailProduct" class="product-details-wrapper">
          <ProductDetails
            :key="`productDetails-${detailProduct.id}`"
            :product-type-id="detailProduct.id"
            :appearance-id="getRelevantAppearanceId(detailProduct)"
            @close="() => (detailProduct = null)"
            @select="() => editProductType(detailProduct)"
          />
        </div>
      </div>
    </template>
  </DetailPage>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';

import { useCompositionStore } from 'commons/stores/composition';
import DetailPage from 'src/app/components/detailPage/DetailPage.vue';
import {
  CategoriesTree,
  CompositionSpecificCategoriesTree,
  CompositionSpecificCategoryProductType,
  TranslatedAppearanceGroup,
} from 'commons/composition/compositionCategoryHelper';
import LazyAssortmentCard from './LazyAssortmentCard.vue';
import ProductSettingsAssortmentFilter from 'src/app/partnerarea/publishing/detail/productSettings/advanced/filters/ProductSettingsAssortmentFilter.vue';
import ProductSettingsFilters from 'src/app/partnerarea/publishing/detail/productSettings/advanced/filters/ProductSettingsFilters.vue';
import { HotspotKey } from '@spreadgroup/common-partner-lib/states';
import { i18n } from 'commons/translate/i18n';
import {
  applyFilters,
  useProductTypeFilters,
} from 'src/app/partnerarea/publishing/detail/productSettings/advanced/filters/productTypeFilters.ts';
import ProductDetails from './ProductDetails.vue';
import CompositionModeBadge from 'src/app/components/publishing/statusBadges/CompositionModeBadge.vue';
import { getAllProductTypes } from '@spreadgroup/common-partner-lib/composition';

const { t } = i18n.global;

const emit = defineEmits<{
  addNew: [productType: number, appearanceId?: number];
  onBack: [];
}>();

const detailProduct = ref<CompositionSpecificCategoryProductType | null>(null);
enum ContextMenuAction {
  PRODUCT_DETAILS = 'PRODUCT_DETAILS',
}
const contextMenuOptions = [
  {
    key: ContextMenuAction.PRODUCT_DETAILS,
    name: t('PUBLISHING.DETAIL.PRODUCTS.ADVANCED.PRODUCT_DETAILS.HEADING'),
  },
];

type ActiveLevel = Pick<CategoriesTree, 'id' | 'names'>;
const activeFirstLevel = ref<ActiveLevel | undefined>();
const activeSecondLevel = ref<ActiveLevel | undefined>();

const onFilterUpdateFirstLevel = (activeLevel?: ActiveLevel) => {
  activeFirstLevel.value = activeLevel;
};

const onFilterUpdateSecondLevel = (activeLevel?: ActiveLevel) => {
  activeSecondLevel.value = activeLevel;
};

const {
  activeAppearanceGroups,
  activeHotspots,
  activeBrands,
  activeTargetGroups,
  activeSizeGroups,
} = useProductTypeFilters();

const filters = computed(() => ({
  activeAppearanceGroups: activeAppearanceGroups.value,
  activeHotspots: activeHotspots.value,
  activeBrands: activeBrands.value,
  activeTargetGroups: activeTargetGroups.value,
  activeSizeGroups: activeSizeGroups.value,
}));

const onFilterUpdateAppearanceGroups = (
  groups: TranslatedAppearanceGroup[],
) => {
  activeAppearanceGroups.value = groups;
};

const onFilterUpdateTargetGroups = (targetGroups: string[]) => {
  activeTargetGroups.value = targetGroups;
};

const onFilterUpdateHotspots = (hotspots: HotspotKey[]) => {
  activeHotspots.value = hotspots;
};

const onFilterUpdateSizeGroups = (sizeGroups: string[]) => {
  activeSizeGroups.value = sizeGroups;
};

type BrandName = CompositionSpecificCategoryProductType['brandName'];

const onFilterUpdateBrands = (brands: BrandName[]) => {
  activeBrands.value = brands;
};

const compositionStore = useCompositionStore();
const compositionMode = computed(() => compositionStore.mode);
const compositionCategories = computed(
  () => compositionStore.categories as CompositionSpecificCategoriesTree,
);

const editProductType = (
  productType?: CompositionSpecificCategoryProductType,
) => {
  if (!productType) return;

  detailProduct.value = null;
  emit('addNew', productType.id, getRelevantAppearanceId(productType));
};

const assortment = computed<CompositionSpecificCategoriesTree[]>(() => {
  if (!compositionCategories.value) return [];
  return (compositionCategories.value.children.map((child) => ({
    ...child,
    children: child.children.map((subChild) => ({
      ...subChild,
      productTypes: getAllProductTypes(
        subChild,
      ) as CompositionSpecificCategoryProductType[],
    })),
  })) || []) satisfies CompositionSpecificCategoriesTree[];
});

const allProductTypes = computed<CompositionSpecificCategoryProductType[]>(
  () => {
    if (!compositionCategories.value) return [];

    return getAllProductTypes(
      compositionCategories.value,
    ) as CompositionSpecificCategoryProductType[];
  },
);

const selectedProductTypes = computed<CompositionSpecificCategoryProductType[]>(
  () => {
    if (!assortment.value.length) return [];

    const { id: activeSecondLevelId } = activeSecondLevel.value || {};
    if (activeSecondLevelId) {
      for (const firstLevel of assortment.value) {
        const secondLevel = firstLevel.children.find(
          (child) => child.id === activeSecondLevelId,
        );
        if (secondLevel) {
          return secondLevel.productTypes || [];
        }
      }
      return [];
    }

    const { id: activeFirstLevelId } = activeFirstLevel.value || {};
    if (activeFirstLevelId) {
      const firstLevel = assortment.value.find(
        (item) => item.id === activeFirstLevelId,
      );
      if (firstLevel) {
        const allProductTypes: CompositionSpecificCategoryProductType[] = [];
        firstLevel.children.forEach((secondLevel) => {
          if (secondLevel.productTypes) {
            allProductTypes.push(...secondLevel.productTypes);
          }
        });
        return allProductTypes;
      }
      return [];
    }

    const allProductTypes: CompositionSpecificCategoryProductType[] = [];
    assortment.value.forEach((firstLevel) => {
      firstLevel.children.forEach((secondLevel) => {
        if (secondLevel.productTypes) {
          allProductTypes.push(...secondLevel.productTypes);
        }
      });
    });
    return allProductTypes;
  },
);

const selectedProductTypesFiltered = computed<
  CompositionSpecificCategoryProductType[]
>(() => {
  return applyFilters(selectedProductTypes.value, filters.value);
});

const getRelevantAppearanceId = (
  productType: CompositionSpecificCategoryProductType,
): number | undefined => {
  if (!activeAppearanceGroups.value.length) {
    return productType.defaultAppearance;
  }

  // find first matching appearance id from any of the active appearance groups
  for (const group of activeAppearanceGroups.value) {
    const { appearanceIds } = group || {};
    const matchingId = productType.appearanceIds.find((id) =>
      appearanceIds?.includes(id),
    );
    if (matchingId) return matchingId;
  }

  return undefined;
};

const contextMenuClick = (
  productType: CompositionSpecificCategoryProductType,
  option: { key: string; name: string },
) => {
  switch (option.key) {
    case ContextMenuAction.PRODUCT_DETAILS:
      detailProduct.value = productType;
      break;
  }
};
</script>

<style lang="scss" scoped>
@import 'src/scss/styleguide/colors';
@import 'src/scss/styleguide/links';
@import 'src/scss/constants';
@import 'src/scss/styleguide/type';

.catalog {
  display: flex;
  width: 100%;
  position: relative;
}

.assortment-toggle-button {
  position: absolute;
  left: 2px;
  top: 50%;
  transform: translateY(-50%);
  z-index: 10;
  padding: 12px 14px 12px 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

  .icon {
    width: 16px;
    height: 16px;
  }
}

.catalog-content {
  width: 100%;
  padding: 24px;

  & > div {
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 16px;
  }
}

.catalog.has-details {
  .catalog-content > div {
    padding-right: 350px + 24px;
  }
}

.assortment-wrapper {
  width: 280px;
  padding: 16px 0 16px 16px;
  display: flex;
  flex-direction: column;

  & > .assortment {
    background-color: $white;
    padding: 16px;
    border: 1px solid $grey15;
    border-radius: $border-radius-medium;
  }
}

.catalog-applied-filters {
  width: 100%;
}

.product-type-list {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 16px;
  width: 100%;
  max-width: none !important;
}

.product-details-wrapper {
  position: absolute;
  top: 24px;
  right: 24px;
  width: 350px;
  bottom: 24px;
}
</style>
